<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="all">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>

          <b-field>
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
              </option>
            </b-select>
          </b-field>

          <b-field>
            <b-select
              v-model="selectedSubarea"
              :loading="loadingSubareas"
              :placeholder="
                $t('message.please_select') + $t('attributes.offer_subarea')
              "
              @input="onSelectSubArea"
            >
              <option value="">
                {{ $t('attributes.all_offer_subarea') }}
              </option>
              <option
                v-for="(option, index) in subareas"
                :value="option"
                :key="index"
              >
                {{ option }}
              </option>
            </b-select>
          </b-field>

          <b-field>
            <b-datepicker
              :placeholder="
                $t('message.please_select') + $t('attributes.date_range')
              "
              v-model="selectedDates"
              range
              @input="onSelectedDates"
            >
              <button class="button is-danger" @click="onClearDates">
                <b-icon icon="close"></b-icon>
                <span>Clear</span>
              </button>
            </b-datepicker>
          </b-field>

          <b-field>
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.offer') }}
          </b-button>
        </div>
      </div>
    </nav>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <span class="is-valid offer-top-desc">上架且未過效期</span>
            <span class="is-invalid offer-top-desc">上架但效期已過</span>
            <span class="is-off offer-top-desc">下架</span>
          </b-field>
        </div>
      </div>
    </nav>
    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      :row-class="setRowClass"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="parseInt(page)"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :visible="false"
        :label="$t('attributes.id')"
        width="100"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="area_code"
        :label="$t('attributes.area')"
        width="180"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.area_name }}
      </b-table-column>

      <b-table-column
        field="subarea"
        :label="$t('attributes.offer_subarea')"
        width="180"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.subarea }}
      </b-table-column>

      <b-table-column
        field="topic"
        :label="$t('attributes.topic')"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.topic }}
      </b-table-column>

      <b-table-column
        field="create_time"
        :label="$t('attributes.create_date')"
        width="180"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.create_time) }}
      </b-table-column>

      <b-table-column
        field="start_date"
        :label="$t('attributes.offer_dates')"
        width="220"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDates([props.row.start_date, props.row.end_date]) }}
      </b-table-column>

      <b-table-column :width="190" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button
            type="is-primary"
            @click.prevent="onModify(props.row)"
            style="margin-left: 5px"
          >
            {{ $t('attributes.modify') }}
          </b-button>
          <a
            :href="offerPreviewUrl(props.row)"
            target="_blank"
            class="button is-primary"
            style="margin-left: 20px"
          >
            {{ $t('attributes.preview') }}
          </a>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <offer-form :offer="selectedItem" @send="onSend" />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { AREA_API, OFFERS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import OfferForm from './OfferForm'

export default {
  name: 'OfferList',
  components: { OfferForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    let startDate = null
    let endDate = null
    let selectedDates = []

    if (this.query.start && this.query.end) {
      startDate = this.$dayjs.unix(this.query.start).toDate()
      endDate = this.$dayjs.unix(this.query.end).toDate()
      selectedDates = [startDate, endDate]
    }

    return {
      loading: false,
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      filterStatus: this.query.status || 'true',
      search: this.query.search || '',
      sortField: this.query.sort || 'sort',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      startDate,
      endDate,
      selectedDates: selectedDates,

      loadingAreas: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,

      loadingSubareas: false,
      subareas: [],
      selectedSubarea: this.query.sa || null,

      isFormModalActive: false,
      sending: false
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return (
        this.loading ||
        this.loadingAreas ||
        this.loadingSubareas ||
        this.loadingItems
      )
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      await this.loadAreas()
      await this.loadSubareas()
      await this.loadItems()
      this.loading = false
    },
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.selectedAreaCode = this.selectedArea && this.selectedArea.code
        } else {
          this.selectedArea = null
          this.selectedSubarea = null
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedSubarea = null
        this.selectedAreaCode = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadSubareas() {
      if (!this.selectedAreaCode) {
        this.subareas = []
        this.selectedSubarea = null
        return
      }

      try {
        const api = `${OFFERS_API}/subareas`

        const params = {
          code: this.selectedAreaCode
        }

        this.loadingSubareas = true
        const res = await this.axios.get(api, { params })
        this.subareas = res.data.data

        if (
          this.selectedSubarea &&
          this.subareas.findIndex(s => s === this.selectedSubarea) === -1
        ) {
          this.selectedSubarea = null
        }
      } catch (e) {
        this.selectedSubarea = null
      } finally {
        this.loadingSubareas = false
      }
    },
    async loadItems() {
      try {
        const api = `${OFFERS_API}`

        let active = null
        if (this.filterStatus === 'true') {
          active = true
        } else if (this.filterStatus === 'false') {
          active = false
        }

        const params = {
          active,
          type: 'C',
          area_code: this.selectedAreaCode,
          subarea: this.selectedSubarea,
          search: this.search,
          start_date: this.startDate,
          end_date: this.endDate,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('offers', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || 'true'
      this.search = query.search || ''
      this.sortField = query.sort || 'sort'
      this.sortOrder = query.order || 'asc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      this.selectedAreaCode = query.ac || null
      this.selectedSubarea = query.sa || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
        this.selectedAreaCode = this.selectedArea && this.selectedArea.code
      } else {
        this.selectedArea = null
      }

      if (this.selectedArea) {
        if (
          this.selectedSubarea &&
          this.subareas.findIndex(s => s === this.selectedSubarea) === -1
        ) {
          this.selectedSubarea = null
        }
      } else {
        this.selectedSubarea = null
      }

      if (query.start && query.end) {
        this.startDate = this.$dayjs.unix(query.start).toDate()
        this.endDate = this.$dayjs.unix(query.end).toDate()
        this.selectedDates = [this.startDate, this.endDate]
      } else {
        this.startDate = null
        this.endDate = null
        this.selectedDates = []
      }
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        ac: this.selectedAreaCode,
        sa: this.selectedSubarea,
        search: this.search,
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix(),
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.page = 1
      this.updateQuery()
    },
    async onSelectArea(option) {
      this.page = 1
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null

      await this.loadSubareas()
      await this.updateQuery()
    },
    onSelectSubArea(option) {
      this.page = 1
      this.selectedSubarea = option

      this.updateQuery()
    },
    onSelectedDates() {
      this.page = 1
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.updateQuery()
    },
    onClearDates() {
      this.page = 1
      this.startDate = null
      this.endDate = null
      this.selectedDates = []

      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },
    setRowClass(row) {
      if (!row.status) {
        return 'is-off'
      } else {
        return this.checkOfferDateValid(row.start_date, row.end_date)
          ? 'is-valid'
          : 'is-invalid'
      }
    },
    checkOfferDateValid(start_date, end_date) {
      const nowDate = new Date()
      return (
        Date.parse(start_date) <= nowDate && Date.parse(end_date) >= nowDate
      )
    },
    onAdd() {
      this.selectedItem = {
        sort: 10,
        status: false,
        type: 'C'
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(OFFERS_API, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadSubareas()
        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${OFFERS_API}/${item.id}`

        this.sending = true
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadSubareas()
        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
